import { Link } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { Reveal } from "@/components/Reveal";
import ArtworkImage from "@/components/ArtworkImage";
import { eur, AVAILABILITY, artText } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import Seo from "@/components/Seo";

export default function Shop() {
  const { artworks, loading } = useStore();
  const { t, lang } = useLang();

  return (
    <div data-testid="shop-page" className="mx-auto max-w-7xl px-6 pb-28 pt-32 sm:px-10 lg:px-16">
      <Seo title={t("seo.shopTitle")} description={t("seo.shop")} />
      <Reveal>
        <p className="eyebrow">{t("shop.eyebrow")}</p>
        <h1 className="mt-4 font-serif text-4xl font-light text-bone sm:text-5xl lg:text-6xl">
          {t("shop.title")}
        </h1>
        <p className="mt-5 max-w-xl text-sm leading-relaxed text-bone-dim/80 sm:text-base">
          {t("shop.intro")}
        </p>
        <p className="mt-6 font-mono text-[10px] tracking-[0.3em] text-bone-muted">
          {loading
            ? "…"
            : t("shop.count")
                .replace("{n}", artworks.length)
                .replace("{y}", new Date().getFullYear())}
        </p>
      </Reveal>

      <div className="mt-16 grid gap-x-10 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
        {artworks.map((art, i) => {
          const availCls = AVAILABILITY[art.availability] || AVAILABILITY["In Atelier"];
          return (
            <Reveal key={art.artwork_id} delay={(i % 3) * 0.1}>
              <Link
                to={`/shop/${art.artwork_id}`}
                className="group block"
                data-testid={`artwork-card-${art.artwork_id}`}
              >
                <ArtworkImage art={art} />
                <div className="mt-5 flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-serif text-xl leading-tight text-bone transition-colors duration-300 group-hover:text-brass sm:text-2xl">
                      {artText(art, "title", lang)}
                    </h2>
                    <p className="mt-2 font-mono text-[9px] tracking-[0.25em] text-bone-muted">
                      {art.dwg_no} · {art.dimensions} · {art.year}
                    </p>
                    <span
                      data-testid={`availability-${art.artwork_id}`}
                      className={`mt-3 inline-block border px-2 py-1 font-mono text-[8px] tracking-[0.25em] ${availCls}`}
                    >
                      {t(`avail.${art.availability}`).toUpperCase()}
                    </span>
                  </div>
                  <span className="whitespace-nowrap font-mono text-sm text-brass">
                    {art.price > 0 ? eur(art.price) : t("product.priceOnRequest")}
                  </span>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </div>

      {!loading && artworks.length === 0 && (
        <p className="mt-20 text-center font-mono text-[11px] tracking-[0.3em] text-bone-muted">
          {t("shop.empty")}
        </p>
      )}
    </div>
  );
}
